import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { DrawerLink } from '../../app/DrawerLink';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { LoadingPanel } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { PlatformIcon, useLinkPlatforms } from '../profile/links';
import { useRoomTuning } from '../../stores/room';
import { Artwork } from '../dashboard/Artwork';
import { ChatPanel } from './ChatPanel';
import { CrateDialog } from './CrateDialog';
import { isListeningTo, startListening, stopListening } from './listening';
import { QueueTable } from './QueueTable';
import { ReviewPanel } from './ReviewPanel';
import { OverlayDialog } from './OverlayDialog';
import { SessionPanel } from './SessionPanel';
import { positionNow } from './sync';
import { useRoom } from './useRoom';
import { duration, elapsed, rooms, type Room, type RoomView } from './api';

/**
 * The DJ console (dev/design/GetVibed DJ Console.dc.html).
 *
 * ## What is here and what is not
 *
 * The design draws a mic toggle, a crossfade slider and a mic/music balance.
 * None of them are here, because nothing is mixing audio: a room broadcasts a
 * *position* in the catalogue, not a stream, so there is no signal path for a
 * voice to sit in. Drawing the controls anyway would be three dead sliders on
 * the busiest screen in the product.
 *
 * They arrive with `source: 'stream'`, which the schema already carries.
 *
 * The payout panel is likewise absent: boosts have tiers and no money, so there
 * is no figure to show that would not be invented.
 *
 * ## The transport moves the room, not this browser
 *
 * Pressing skip here advances the *room's* playhead for everybody. It does not
 * start audio in this tab - the console is silent for now, and the synchronised
 * player that makes a host hear what their listeners hear is the next piece.
 * That distinction is worth keeping straight even once audio lands: the deck is
 * a remote control, not a media player.
 */
export function ConsolePage() {
  const queryClient = useQueryClient();

  const [crateOpen, setCrateOpen] = useState(false);
  // Subscribed rather than read once, so the header repaints when it changes.
  const tuned = useRoomTuning((state) => state.tuned);

  /*
   * `mine` rather than a username in the URL. One person has one station, so
   * there is nothing to choose between - and this endpoint creates it on first
   * use, which is why a host never fills in a form to start broadcasting.
   */
  const { data, isPending, error } = useRoom(undefined, { listen: true, mine: true });

  if (isPending) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <LoadingPanel label="Opening the console" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <Alert tone="error">
          {error instanceof ApiError ? error.message : 'That room could not be loaded.'}
        </Alert>
      </div>
    );
  }

  const room = data.room;

  if (!room) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-start gap-4 px-6 py-16">
        <Alert tone="error">You are not set up to host.</Alert>
        <Link to="/host" className="text-sm text-accent hover:underline">
          Apply to host →
        </Link>
      </div>
    );
  }

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['room', 'mine'] });

  return (
    /*
      Exactly a screen tall, and nothing outside a pane scrolls.

      Three columns that each grow to fit made the *document* the scroller, so
      reading the bottom of a long queue scrolled the deck and the chat away
      with it - on the one screen where all three are being watched at once.
      A console is an instrument panel, not a document.

      `h-` rather than `min-h-`, so the block cannot grow past the viewport, and
      `overflow-hidden` so a pane that overflows is that pane's problem. The
      height subtracts the header *and* the dock, which is fixed over the bottom
      76px of every page - without that the chat input lands underneath it.

      `-mb-6` cancels the shell's bottom padding, which would otherwise put a
      viewport-height block on a page 1.5rem taller than the viewport and give
      back the body scrollbar this exists to remove. Same recipe as Messages.

      The height subtracts `--spacing-chrome-top`, not `4rem`: the header is
      64px of content and a 65th of border, and a screen sized to `4rem` leaves
      exactly one pixel of page scroll - which is enough to keep the scrollbar
      and undo the whole thing.

      All of it from `lg` up. Below that the columns stack, and three panes
      sharing one screen's height would be three unusable slivers - so the page
      scrolls the way every other page does.
    */
    <div
      className="flex flex-col lg:-mb-6 lg:h-[calc(100dvh-var(--spacing-chrome-top)-var(--spacing-dock))] lg:overflow-hidden"
      data-console
    >
      <ConsoleHeader
        room={room}
        view={data}
        onChange={refresh}
        monitoring={tuned?.username === room.username}
        /*
         * The same global tuning a listener uses, so a host monitoring their own
         * broadcast gets the dock's live view and keeps hearing it after they
         * navigate away from the console.
         */
        onMonitor={() => (isListeningTo(room.username) ? stopListening() : startListening(room))}
      />

      {/*
        Three columns at desk width, stacked below it. A host on a phone is not
        the case this screen is for, but a host checking their queue from one is
        ordinary and the page should not be unusable.

        The side columns are clamped rather than fixed. Both are dense - the deck
        carries artwork, a transport and eight settings; the rail carries session
        figures and a chat - and on a wide display a fixed 320px leaves them
        cramped while the queue in the middle runs to nothing but whitespace.
        They grow with the viewport and stop, so a 1280px laptop keeps the
        proportions the queue needs and a 27" monitor spends its extra width on
        the panels that can use it.
      */}
      <div
        className="grid flex-1 gap-px bg-line lg:min-h-0 lg:grid-cols-[minmax(0,var(--console-deck))_minmax(0,3fr)_minmax(0,var(--console-rail))]"
        style={
          {
            '--console-deck': 'clamp(320px, 25vw, 440px)',
            '--console-rail': 'clamp(340px, 26vw, 460px)',
          } as React.CSSProperties
        }
      >
        <Deck room={room} onChange={refresh} />

        {/* Each pane owns its own overflow. */}
        <section
          className="flex min-w-0 flex-col gap-5 bg-app p-6 lg:min-h-0 lg:overflow-y-auto"
          data-pane="queue"
        >
          <QueueTable room={room} view={data} onChange={refresh} onAdd={() => setCrateOpen(true)} />
        </section>

        {/*
          No padding on the column itself - each scrolling child carries its
          own, so its scrollbar sits against the edge of the pane rather than
          floating 24px inside it with a strip of dead space beyond. A
          scrollbar drawn short of the edge it belongs to looks like a mistake,
          because it is one.
        */}
        <aside className="flex min-w-0 flex-col bg-app lg:min-h-0" data-pane="session">
          {/*
            The figures keep their size and the chat takes what is left, so the
            message box sits at the bottom of the column rather than wherever
            the conversation happens to end. `ChatPanel` is already
            `flex-1 min-h-0` with its own scroller - it just needed a parent
            with a height to be a fraction of.
          */}
          {/*
            The figures take the column when there is no chat under them, and
            share it when there is. A `max-h` on a panel with nothing below it
            would be a scrollbar inside empty space.
          */}
          <div
            className={`overflow-y-auto px-6 pt-6 ${
              room.simulcast ? 'min-h-0 flex-1' : 'shrink-0 lg:max-h-[55%]'
            }`}
          >
            <SessionPanel room={room} view={data} />

            {/*
              Said, rather than the chat just being gone.

              A host who set a simulcast this morning and opens the console
              tonight would otherwise find a missing panel and no reason for it.
              One line is cheaper than the support question.
            */}
            {room.simulcast && (
              <p
                data-chat-elsewhere
                className="mt-6 border-t border-line pt-5 font-mono text-[10px] leading-relaxed text-ghost"
              >
                room chat is off while you are streaming on {room.simulcast.label} — the
                conversation is over there, and a second empty one here would only split it
              </p>
            )}
          </div>

          {!room.simulcast && (
            <div className="flex min-h-0 flex-1 flex-col pb-6 pt-6">
              <ChatPanel username={room.username} canModerate />
            </div>
          )}
        </aside>
      </div>

      {crateOpen && <CrateDialog onClose={() => setCrateOpen(false)} onAdded={refresh} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Header                                                                      */
/* -------------------------------------------------------------------------- */

function ConsoleHeader({
  room,
  view,
  onChange,
  monitoring,
  onMonitor,
}: {
  room: Room;
  view: RoomView;
  onChange: () => void;
  monitoring: boolean;
  onMonitor: () => void;
}) {

  // Ticks so the on-air clock counts. One second is the resolution it displays.
  const [, tick] = useState(0);

  useEffect(() => {
    if (room.status !== 'live') return;

    const timer = setInterval(() => tick((n) => n + 1), 1000);

    return () => clearInterval(timer);
  }, [room.status]);

  const queryClient = useQueryClient();

  const broadcast = useMutation({
    mutationFn: () => (room.status === 'live' ? rooms.endBroadcast() : rooms.goLive()),
    onSuccess: () => {
      onChange();

      /*
       * Everything that reports who is on air, refreshed now rather than on its
       * own schedule.
       *
       * `useLiveHosts` polls every 45 seconds, which is fine for noticing that
       * somebody *else* went live and useless for your own broadcast: the
       * header count, the dashboard rail and the account menu's "You're on air"
       * would all have kept insisting for the best part of a minute after this
       * host pressed End - and the one person guaranteed to be looking at them
       * is the one who just pressed it.
       */
      void queryClient.invalidateQueries({ queryKey: ['live-hosts'] });
    },
  });

  return (
    <header className="flex flex-wrap items-center gap-5 border-b border-line bg-deep px-6 py-3.5">
      {room.status === 'live' ? (
        <span className="flex shrink-0 items-center gap-2.5 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-4 py-2">
          <span className="h-2 w-2 rounded-full bg-hot" aria-hidden />
          <span className="meta-caps text-hot-text">On air · {elapsed(room.live_started_at)}</span>
        </span>
      ) : (
        <span className="shrink-0 rounded-[var(--radius-pill)] border border-line px-4 py-2">
          <span className="meta-caps text-faint">Off air</span>
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* The set's name if it has one, otherwise the person - because the
            broadcast is theirs and that is enough of an identity. */}
        <span className="truncate text-base font-bold tracking-tight text-ink">
          {room.name ?? room.host.display_name}
        </span>
        <span className="truncate font-mono text-[11px] text-soft">
          /{room.username} · {room.submissions} submissions
        </span>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-6">
        <Stat value={room.listener_count} label="listening" />
        <Stat value={view.session.queued} label="in queue" tone="text-fresh" />
        <Stat value={view.session.boosted} label="boosted" tone="text-accent" />
      </div>

      {broadcast.isError && (
        <span className="text-sm text-hot-text">
          {broadcast.error instanceof ApiError ? broadcast.error.message : 'That did not work.'}
        </span>
      )}

      {/*
        A toggle, not a one-way door.

        It used to disappear once pressed, which left a host with no way to tell
        whether they were monitoring and no way to stop - and turning your own
        broadcast off in your ear is a thing hosts do constantly, because it is
        how you talk to somebody in the room without hearing yourself think.
      */}
      {room.status === 'live' && (
        <Button
          variant={monitoring ? 'secondary' : 'primary'}
          onClick={onMonitor}
          data-monitor
          aria-pressed={monitoring}
        >
          {monitoring ? 'Monitoring' : 'Monitor'}
        </Button>
      )}

      <Button
        variant={room.status === 'live' ? 'danger' : 'primary'}
        loading={broadcast.isPending}
        onClick={() => broadcast.mutate()}
        data-broadcast-toggle
      >
        {room.status === 'live' ? 'End broadcast' : 'Go on air'}
      </Button>
    </header>
  );
}

function Stat({ value, label, tone = 'text-ink' }: { value: number; label: string; tone?: string }) {
  return (
    <div className="flex flex-col">
      <span className={`text-lg font-bold ${tone}`}>{value.toLocaleString()}</span>
      <span className="font-mono text-[10px] text-faint">{label}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Deck                                                                        */
/* -------------------------------------------------------------------------- */

function Deck({ room, onChange }: { room: Room; onChange: () => void }) {
  const [position, setPosition] = useState(0);
  const [reviewing, setReviewing] = useState(false);

  /*
   * The deck's clock runs locally between server updates.
   *
   * Asking the server four times a second for a number it already told us how
   * to compute would be absurd; `positionNow` extrapolates from the last known
   * position and the corrected clock, and every poll or pushed event snaps it
   * back to the truth.
   */
  useEffect(() => {
    const update = () => {
      const at = positionNow(room) ?? 0;
      const length = room.now_playing?.duration_ms;

      /*
       * Clamped to the track's length.
       *
       * The server advances a finished room on the next read, so this only ever
       * shows for about a second - but a bar reading 3:39 of 3:15 is wrong for
       * that second, and the reader has no way to know it is about to correct
       * itself.
       */
      setPosition(length == null ? at : Math.min(at, length));
    };

    update();
    const timer = setInterval(update, 250);

    return () => clearInterval(timer);
  }, [room]);

  const transport = useMutation({
    mutationFn: (action: 'play' | 'pause' | 'next') => rooms.transport(action),
    onSuccess: onChange,
  });

  const live = room.status === 'live';
  const track = room.now_playing;
  const remaining = track?.duration_ms == null ? null : Math.max(0, track.duration_ms - position);

  /*
   * Held: the track finished and auto-play is off, so the room is waiting on
   * the host rather than stalled. Worth distinguishing on the deck, because the
   * controls mean different things - play takes the next track rather than
   * resuming this one, which the server does and the button should say.
   */
  const held = room.holding;

  return (
    <section
      className="flex min-w-0 flex-col gap-6 bg-app p-6 lg:min-h-0 lg:overflow-y-auto"
      data-deck
    >
      <div className="flex items-center justify-between">
        <span className="meta-caps text-faint">
          {held ? 'Deck · finished' : 'Deck · now playing'}
        </span>
        <span className="font-mono text-[11px] text-faint">{room.source}</span>
      </div>

      {/*
        Said plainly, because silence in a live room is alarming and the host
        needs to know instantly whether it is their doing. This is the state
        auto-play off exists to produce, so it reads as a step rather than a
        fault - and it names the thing to do next.
      */}
      {held && (
        <p
          data-held
          className="rounded-[var(--radius-control)] border border-fresh-edge bg-fresh-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-fresh"
        >
          Finished. The room is holding — review it, then play the next.
        </p>
      )}

      {track ? (
        <>
          <Artwork
            track={{ title: track.title, artwork: track.artwork, artwork_small: track.artwork_small }}
            large
            className="aspect-square w-full max-w-[200px] rounded-[var(--radius-panel)]"
          />

          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-bold leading-tight tracking-tight text-ink">{track.title}</h2>
            <DrawerLink
              to={`/${track.artist.username}`}
              className="text-[15px] text-text-2 hover:text-ink hover:underline"
            >
              {track.artist.display_name}
            </DrawerLink>
          </div>

          {/* A plain elapsed bar, not the design's waveform. The waveform peaks
              are already fetched per track for the dock; wiring them here is
              worth doing once the deck is otherwise settled, and a progress bar
              that is accurate beats a waveform that is decorative. */}
          <div className="flex flex-col gap-2">
            <div className="h-1 overflow-hidden rounded-[var(--radius-pill)] bg-[#22224a]">
              <div
                className="h-full rounded-[var(--radius-pill)] bg-accent transition-[width] duration-200"
                style={{
                  width: `${track.duration_ms ? Math.min(100, (position / track.duration_ms) * 100) : 0}%`,
                }}
              />
            </div>
            <div className="flex justify-between font-mono text-[11px] text-soft">
              <span data-deck-position>{duration(position)}</span>
              <span>{remaining === null ? '—' : `-${duration(remaining)}`}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="flex aspect-square w-full max-w-[200px] items-center justify-center rounded-[var(--radius-panel)] border border-line bg-surface">
          <p className="px-6 text-center text-sm text-soft">
            {live ? 'Nothing playing. Press play to take the next submission.' : 'The room is off air.'}
          </p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          full
          disabled={!live}
          loading={transport.isPending}
          onClick={() => transport.mutate(room.paused || !track ? 'play' : 'pause')}
          data-transport-play
        >
          {held ? 'Play next' : room.paused || !track ? 'Play' : 'Pause'}
        </Button>
        <Button
          variant="secondary"
          disabled={!live}
          onClick={() => transport.mutate('next')}
          data-transport-next
        >
          Skip
        </Button>
      </div>

      {transport.isError && (
        <Alert tone="error">
          {transport.error instanceof ApiError ? transport.error.message : 'The deck did not respond.'}
        </Alert>
      )}

      {/*
        What a host does about the track that is playing, as opposed to what
        they do to the deck.

        The design draws Follow here too. It is absent because `FollowButton`
        needs a loaded profile and the deck has a username - fetching one per
        track change to render a button nobody presses mid-set is the wrong
        trade. The artist's name above is a link to where following lives.
      */}
      {track && (
        <div className="grid grid-cols-2 gap-2.5 border-t border-line pt-5">
          <FavoriteButton
            trackId={track.id}
            favorited={false}
            className="justify-center rounded-[var(--radius-control)] border border-line py-3"
          />
          <button
            type="button"
            aria-pressed={reviewing}
            data-review-toggle
            onClick={() => setReviewing((open) => !open)}
            className={`rounded-[var(--radius-control)] border py-3 text-[13px] font-bold transition-colors ${
              reviewing
                ? 'border-transparent bg-accent text-accent-ink'
                : 'border-line text-ink hover:border-line-strong'
            }`}
          >
            Review
          </button>
        </div>
      )}

      {/*
        Keyed on the track, so skipping mid-sentence does not leave one artist's
        review in a box now labelled with another's name. It closes the panel
        too - the words are gone either way, and a host who meant to keep them
        would rather find that out with an empty box than send them to the
        wrong person.
      */}
      {track && reviewing && (
        <ReviewPanel key={track.id} trackId={track.id} onClose={() => setReviewing(false)} />
      )}

      <SimulcastSetting room={room} onChange={onChange} />

      <AutoPlaySetting room={room} onChange={onChange} />
      <ShowQueueSetting room={room} onChange={onChange} />

      <SubmissionsSetting room={room} onChange={onChange} />

      <ExplicitSetting room={room} onChange={onChange} />
    </section>
  );
}

/**
 * Where else this set is going out.
 *
 * ## Only somewhere they can actually be watched
 *
 * Two filters, and neither is arbitrary. The platform has to carry live video,
 * because "watch live on Spotify" is a button that goes somewhere with nothing
 * live on it. And they have to have the link on their profile, because that
 * link *is* the badge's address - there is no second field to paste a stream
 * URL into, on purpose, so an artist who changes their Twitch handle changes it
 * once.
 *
 * The consequence is that a host with no eligible links is offered nothing and
 * told why, rather than a dropdown that rejects every choice in it.
 */
function SimulcastSetting({ room, onChange }: { room: Room; onChange: () => void }) {
  const { platforms } = useLinkPlatforms();

  /*
   * Their own links, from their public profile.
   *
   * `/me` is the obvious-looking endpoint and it is the wrong one: it returns
   * the account - identity, roles, permissions - and carries no profile links
   * at all. Reading `links` off it produced `undefined` on every request, so
   * this picker showed its "add a link first" empty state to every host on the
   * site including ones with six links.
   *
   * `/users/{username}` is what the settings page already reads, so it is the
   * same query key and the same normalised shape - add a link over there and it
   * is selectable here without a refetch.
   */
  const { data: profile } = useQuery({
    queryKey: ['profile', room.username],
    queryFn: () => api.get<{ links: { platform: string }[] }>(`/users/${room.username}`),
  });

  const update = useMutation({
    mutationFn: (platform: string | null) => rooms.settings({ simulcast_platform: platform }),
    onSuccess: onChange,
  });

  const eligible = platforms.filter(
    (p) => p.live && (profile?.links ?? []).some((l) => l.platform === p.platform),
  );

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <span className="meta-caps text-faint">Also streaming on</span>

      {eligible.length === 0 ? (
        <p className="font-mono text-[10px] leading-relaxed text-ghost">
          Add a Twitch, YouTube, TikTok, Instagram, Facebook, X or Discord link to your profile and
          you can point listeners at it from here.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            <Choice
              active={room.simulcast === null}
              onClick={() => update.mutate(null)}
              disabled={update.isPending}
              data-simulcast="none"
            >
              Audio only
            </Choice>

            {eligible.map((p) => (
              <Choice
                key={p.platform}
                active={room.simulcast?.platform === p.platform}
                onClick={() => update.mutate(p.platform)}
                disabled={update.isPending}
                data-simulcast={p.platform}
              >
                <PlatformIcon platform={p.platform} className="h-3.5 w-3.5" />
                {p.label}
              </Choice>
            ))}
          </div>

          {room.simulcast && (
            <p className="truncate font-mono text-[10px] text-ghost">
              listeners get a link to {room.simulcast.url}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Choice({
  active,
  children,
  ...rest
}: {
  active: boolean;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-pressed={active}
      {...rest}
      className={`flex items-center gap-1.5 rounded-[var(--radius-pill)] border px-3 py-1.5 font-mono text-[11px] transition-colors ${
        active
          ? 'border-transparent bg-accent text-accent-ink'
          : 'border-line text-soft hover:border-line-strong hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Whether a finished track rolls into the next one.
 *
 * On the deck beside the submissions policy, because both are things a host
 * reaches for *during* a broadcast rather than settings they configured once.
 *
 * Off is the reviewing mode: the room stops at the end of each track, the deck
 * keeps it loaded, and the review box stays pointed at it until the host
 * presses play. The consequence is dead air, so the control says so - a host
 * who turns this on absent-mindedly and walks away should have been told.
 */
function AutoPlaySetting({ room, onChange }: { room: Room; onChange: () => void }) {
  const update = useMutation({
    mutationFn: (auto_play: boolean) => rooms.settings({ auto_play }),
    onSuccess: onChange,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-faint">Auto play</span>

        <button
          type="button"
          role="switch"
          aria-checked={room.auto_play}
          aria-label="Auto play"
          data-auto-play
          disabled={update.isPending}
          onClick={() => update.mutate(!room.auto_play)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            room.auto_play ? 'border-transparent bg-accent' : 'border-line bg-raised'
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              room.auto_play ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        {room.auto_play
          ? 'The next track starts on its own when this one ends.'
          : 'The room holds at the end of each track. Nothing plays until you press play.'}
      </p>
    </div>
  );
}

/**
 * Whether the room shows what is coming up.
 *
 * ## The count stays either way
 *
 * "How many are waiting" is what somebody deciding whether to submit needs, and
 * hiding it would make the submit button a coin toss. This hides the contents:
 * which tracks, in what order, and at what tier. A host running a set where the
 * running order is the show gets to keep it; a host whose boosts are the point
 * gets to publish it.
 *
 * ## Enforced on the server
 *
 * `ShowStationAction` withholds the rows rather than the client hiding them, so
 * turning this off is not one `fetch` away from being undone. This switch is
 * the control, not the mechanism.
 */
function ShowQueueSetting({ room, onChange }: { room: Room; onChange: () => void }) {
  const update = useMutation({
    mutationFn: (show_queue: boolean) => rooms.settings({ show_queue }),
    onSuccess: onChange,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-faint">Show the queue</span>

        <button
          type="button"
          role="switch"
          aria-checked={room.show_queue}
          aria-label="Show the queue"
          data-show-queue
          disabled={update.isPending}
          onClick={() => update.mutate(!room.show_queue)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            room.show_queue ? 'border-transparent bg-accent' : 'border-line bg-raised'
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              room.show_queue ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        {room.show_queue
          ? 'Listeners can see what is coming up, and which submissions were boosted.'
          : 'Listeners see how many are waiting, but not what they are. Your console is unchanged.'}
      </p>
    </div>
  );
}

/**
 * The way in to the stream graphics.
 *
 * ## A button, not a URL
 *
 * There are three graphics and six appearance options behind this, which is
 * more than a settings panel in a sidebar can hold without becoming the sidebar.
 * It is also a thing a host does once while setting up a scene collection and
 * then never again, which is exactly what a dialog is for.
 *
 * ## And nothing of the token out here
 *
 * The console is a screen hosts screen-share while setting up, so the URL - the
 * only credential the overlay has - never renders until somebody opens the
 * dialog and asks for it.
 */
function OverlayLink({ token }: { token: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <span className="meta-caps text-faint">Stream graphics</span>

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        now playing, next in queue and recently played — as browser sources for OBS, Streamlabs
        or TikTok Studio, on a transparent background
      </p>

      <button
        type="button"
        data-overlay-open-dialog
        onClick={() => setOpen(true)}
        className="mt-1 flex items-center justify-center gap-2 rounded-[var(--radius-control)] border border-line px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-line-strong hover:bg-raised"
      >
        <svg viewBox="0 0 16 16" className="h-4 w-4 text-soft" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <rect x="1.5" y="2.5" width="13" height="9" rx="1.5" />
          <path d="M5.5 14h5" />
        </svg>
        Set up overlays
      </button>

      {open && <OverlayDialog token={token} onClose={() => setOpen(false)} />}
    </div>
  );
}

/**
 * Whether this room takes tracks marked explicit.
 *
 * Beside the submissions policy because it is the same kind of decision - who
 * and what gets in - and a host setting one has usually just thought about the
 * other.
 *
 * On by default. A room that silently refused submissions because its owner
 * never opened a setting would be enforcing a rule nobody chose; turning it off
 * is the deliberate act, which is the right way round for a filter.
 */
function ExplicitSetting({ room, onChange }: { room: Room; onChange: () => void }) {
  const update = useMutation({
    mutationFn: (accepts: boolean) => rooms.settings({ accepts_explicit: accepts }),
    onSuccess: onChange,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-faint">Explicit tracks</span>

        <button
          type="button"
          role="switch"
          aria-checked={room.accepts_explicit}
          aria-label="Accept explicit tracks"
          data-accepts-explicit
          disabled={update.isPending}
          onClick={() => update.mutate(!room.accepts_explicit)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            room.accepts_explicit ? 'border-transparent bg-accent' : 'border-line bg-raised'
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              room.accepts_explicit ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        {room.accepts_explicit
          ? 'Anything goes. Tracks the artist marked explicit can be submitted.'
          : 'Refused at the door, and the submitter is told why. This applies to your own crate too.'}
      </p>
    </div>
  );
}

/**
 * Who may put things in the queue.
 *
 * On the deck rather than buried in a settings page, because it is the control
 * a host reaches for *during* a broadcast - it is the answer to being brigaded,
 * and an answer that takes four clicks to find is not an answer.
 */
function SubmissionsSetting({ room, onChange }: { room: Room; onChange: () => void }) {
  const update = useMutation({
    mutationFn: (submissions: Room['submissions']) => rooms.settings({ submissions }),
    onSuccess: onChange,
  });

  const options: { id: Room['submissions']; label: string }[] = [
    { id: 'open', label: 'Everyone' },
    { id: 'followers', label: 'Followers' },
    { id: 'closed', label: 'Closed' },
  ];

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <span className="meta-caps text-faint">Submissions</span>
      <div className="flex gap-1.5">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={room.submissions === option.id}
            data-submissions={option.id}
            onClick={() => update.mutate(option.id)}
            className={`flex-1 rounded-[var(--radius-control)] border px-2 py-2 font-mono text-[11px] transition-colors ${
              room.submissions === option.id
                ? 'border-transparent bg-accent text-accent-ink'
                : 'border-line text-soft hover:border-line-strong hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      {/* The viewer this token was generated for now exists. */}
      {room.overlay_token && <OverlayLink token={room.overlay_token} />}
    </div>
  );
}

export { Deck as ConsoleDeck };
